// Şube listesi (MOD-11 / K-2) — branch.view görüntüler, branch.manage yönetir.
//
// Server-side sayfalama + il filtresi + isActive segmenti + arama (debounce).
// Satır → düzenleme sayfası; silme onaylı. "Yeni şube" branch.manage gate.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Popconfirm, Segmented, Select, Space, Tag, Typography } from 'antd';
import type { TableColumnType } from 'antd';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { DataTable } from '@shared/components/DataTable';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { branchesApi, citiesApi } from '@api/branches';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Branch } from '../types';

const { Text } = Typography;

type ActiveFilter = 'ALL' | 'active' | 'inactive';

export function BranchListPage() {
  const { t } = useTranslation(['branches', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.BRANCH_MANAGE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [cityFilter, setCityFilter] = useState<string | undefined>(undefined);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const resetPage = () => setPage(1);

  const isActive =
    activeFilter === 'active' ? true : activeFilter === 'inactive' ? false : undefined;

  const { data: cities = [] } = useQuery({ queryKey: ['cities'], queryFn: () => citiesApi.list() });

  const { data, isLoading } = useQuery({
    queryKey: ['branches', { debouncedSearch, cityFilter, isActive, page, pageSize }],
    queryFn: () =>
      branchesApi.list({
        search: debouncedSearch || undefined,
        cityId: cityFilter,
        isActive,
        page,
        pageSize,
      }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['branches'] });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => branchesApi.remove(id),
    onSuccess: async () => {
      toast.success(t('toast.deleted'));
      await invalidate();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const goEdit = (id: string) => void navigate(ROUTES.BRANCH_EDIT.replace(':id', id));

  const columns: TableColumnType<Branch>[] = [
    {
      title: t('list.columns.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, r) => (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => goEdit(r.id)}>
          {name || t('list.untitled')}
        </Button>
      ),
    },
    {
      title: t('list.columns.location'),
      key: 'location',
      render: (_, r) => (
        <Space size={4}>
          <MapPin size={13} style={{ color: 'var(--ds-text-tertiary)' }} />
          <Text>{r.cityName}</Text>
          {r.districtName ? <Text type="secondary">/ {r.districtName}</Text> : null}
        </Space>
      ),
    },
    {
      title: t('list.columns.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (v: string | null) => v || '—',
    },
    {
      title: t('list.columns.order'),
      dataIndex: 'order',
      key: 'order',
      width: 90,
      align: 'right',
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</span>,
    },
    {
      title: t('list.columns.status'),
      key: 'isActive',
      width: 110,
      render: (_, r) =>
        r.isActive ? (
          <Tag color="green">{t('common:states.active')}</Tag>
        ) : (
          <Tag>{t('common:states.inactive')}</Tag>
        ),
    },
    {
      title: t('list.columns.updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (v: string) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—'),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      align: 'right',
      width: 110,
      render: (_, r) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<Pencil size={15} />}
            onClick={() => goEdit(r.id)}
            aria-label={t('list.edit')}
            title={t('list.edit')}
          />
          <Popconfirm
            title={t('list.delete.title')}
            description={t('list.delete.confirm', { name: r.name })}
            okText={t('common:actions.delete')}
            okButtonProps={{ danger: true }}
            cancelText={t('common:actions.cancel')}
            onConfirm={() => deleteMutation.mutate(r.id)}
            disabled={!canManage}
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 size={15} />}
              disabled={!canManage}
              aria-label={t('common:actions.delete')}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="branches-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('title') }]}
        actions={
          canManage ? (
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={() => void navigate(ROUTES.BRANCH_NEW)}
              data-testid="branch-new"
            >
              {t('new')}
            </Button>
          ) : null
        }
      />

      <DataTable<Branch>
        data={data?.items ?? []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        search={{
          value: search,
          onChange: (v) => {
            setSearch(v);
            resetPage();
          },
          placeholder: t('list.searchPlaceholder'),
        }}
        pagination={{
          page,
          size: pageSize,
          total: data?.total ?? 0,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        filters={
          <Space wrap>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={t('list.cityFilter')}
              style={{ minWidth: 180 }}
              value={cityFilter}
              onChange={(v?: string) => {
                setCityFilter(v);
                resetPage();
              }}
              options={cities.map((c) => ({ value: c.id, label: `${c.plateCode} · ${c.name}` }))}
            />
            <Segmented<ActiveFilter>
              size="small"
              value={activeFilter}
              onChange={(v) => {
                setActiveFilter(v);
                resetPage();
              }}
              options={[
                { value: 'ALL', label: t('common:labels.all') },
                { value: 'active', label: t('common:states.active') },
                { value: 'inactive', label: t('common:states.inactive') },
              ]}
            />
          </Space>
        }
      />
    </div>
  );
}
